import { getReportBytes } from '../persistence/v2Database';
import { shareReportFiles, type NamedPdf, type ShareAllCode } from '../report/shareAll';
import { cloudReportLink, refreshCloudReports, reportCloudStore } from './reportCloudStore';
import { reportStore } from './reportStore';

// 「先下载全部报告」 (Owner decision list 2026-10-19 item 28; reused when the service shuts down): every PDF on
// this device, plus every PDF in the account that is not on this device (a reinstall, another phone),
// handed over in one go. A cloud copy that cannot be fetched is counted, never silently left out.
export type ExportAllOutcome = { code: ShareAllCode | 'none'; count: number; skipped: number; detail: string };

export interface ExportAllDeps {
  share?: (files: readonly NamedPdf[]) => Promise<{ code: ShareAllCode; detail: string }>;
  fetchImpl?: typeof fetch;
}

export async function exportAllReportsAction(deps: ExportAllDeps = {}): Promise<ExportAllOutcome> {
  const files: NamedPdf[] = [];
  const local = [...reportStore.get().reports].sort((a, b) => a.generatedAt - b.generatedAt || a.id.localeCompare(b.id));
  const onDevice = new Set<string>();
  for (const record of local) {
    const blob = await getReportBytes(record.id);
    if (!blob) continue;
    onDevice.add(record.id);
    files.push({ fileName: record.fileName, blob });
  }
  await refreshCloudReports();
  let skipped = 0;
  const fetcher = deps.fetchImpl ?? fetch;
  for (const file of reportCloudStore.get().files) {
    if (onDevice.has(file.reportId)) continue;
    try {
      const url = await cloudReportLink(file.id);
      const res = url ? await fetcher(url) : null;
      if (!res || !res.ok) {
        skipped += 1;
        continue;
      }
      files.push({ fileName: file.fileName, blob: new Blob([await res.arrayBuffer()], { type: 'application/pdf' }) });
    } catch {
      skipped += 1;
    }
  }
  if (files.length === 0) return { code: 'none', count: 0, skipped, detail: '' };
  const outcome = await (deps.share ?? shareReportFiles)(files);
  return { code: outcome.code, count: files.length, skipped, detail: outcome.detail };
}
